import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import FilterDropdown from '../../../SharedComponents/FilterDropdown';
import FilterButton from '../../../SharedComponents/FilterButton';
import LocationFilterDropdown from './components/LocationFilterDropdown';
import useIsMobile from '../../../../../../components/hooks/useIsMobile';

import styles from '../../../searchV2.scss';

const LocationFilter = (props) => {
    const {
        filters,
        applyLocationFilter,
        geocoded,
        handleLocationChange,
        handleLocationSelect,
        handleCurrentLocationClick,
        handleDistanceChange,
        onMobileClick,
    } = props;

    const [showModal, setShowModal] = useState(false);
    const { isMobileWidth } = useIsMobile();
    const wrapperRef = useRef(null);

    const locationFilterProps = {
        value: filters.locationInputValue || '',
        geoLocation: geocoded,
        handleLocationChange,
        handleLocationSelect,
        handleCurrentLocationClick,
        distance: filters.distance,
        handleDistanceChange,
    };

    const hasCoordinateLocation = filters.location && filters.lat && filters.lng && filters.distance;
    const isActive = Boolean(
        hasCoordinateLocation ||
        filters.locationCountryName ||
        filters.locationStateName ||
        showModal,
    );

    const getDisplayText = () => {
        if (filters.locationCountryName) {
            return filters.locationStateName
                ? `In ${filters.locationStateName}, ${filters.locationCountryName}`
                : `In ${filters.locationCountryName}`;
        }
        if (filters.location && filters.lat && filters.lng && filters.distance) {
            return `Near ${filters.location}`;
        }
        return 'Location';
    };

    const restoreLocationInput = () => {
        // Ensure the input is populated with the current location when modal opens
        if (!filters.locationInputValue && filters.location) {
            handleLocationChange(filters.location);
        } else if (!filters.locationInputValue && filters.locationCountryName) {
            // For country/state filters, use the display text as input value
            const displayLocation = filters.locationStateName
                ? `${filters.locationStateName}, ${filters.locationCountryName}`
                : filters.locationCountryName;
            handleLocationChange(displayLocation);
        }
    };

    const handleFilterClick = () => {
        restoreLocationInput();
        setShowModal(true);
    };

    const handleMobileClick = () => {
        restoreLocationInput();
        if (onMobileClick) {
            onMobileClick();
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleApplyLocation = () => {
        applyLocationFilter();
        setShowModal(false);
    };

    // Handle click outside modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target) && showModal) {
                handleCloseModal();
            }
        };

        if (showModal) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showModal]);

    // On mobile, render just a button that triggers the parent's modal
    if (isMobileWidth && onMobileClick) {
        return (
            <FilterButton
                isActive={isActive}
                onClick={handleMobileClick}
                data-testid="search.events.filter.location-mobile"
            >
                {getDisplayText()}
            </FilterButton>
        );
    }

    // On desktop, use the existing FilterDropdown
    return (
        <div className={styles.filterWrapper} ref={wrapperRef}>
            <FilterButton
                isActive={isActive}
                onClick={handleFilterClick}
                data-testid="search.events.filter.location"
            >
                {getDisplayText()}
            </FilterButton>
            {showModal && (
                <FilterDropdown
                    title={'Location'}
                    onCloseClick={handleCloseModal}
                    handleFilterSubmit={handleApplyLocation}
                    showHeader={false}
                    data-testid="search.events.filters.location.dropdown"
                >
                    <LocationFilterDropdown {...locationFilterProps} />
                </FilterDropdown>
            )}
        </div>
    );
};

LocationFilter.propTypes = {
    filters: PropTypes.instanceOf(Object).isRequired,
    applyLocationFilter: PropTypes.func.isRequired,
    geocoded: PropTypes.instanceOf(Object),
    handleLocationChange: PropTypes.func.isRequired,
    handleLocationSelect: PropTypes.func.isRequired,
    handleCurrentLocationClick: PropTypes.func.isRequired,
    handleDistanceChange: PropTypes.func.isRequired,
    onMobileClick: PropTypes.func,
};

LocationFilter.defaultProps = {
    geocoded: null,
    onMobileClick: null,
};

export default LocationFilter;
